import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { isHoverPreviewOfferType, type ChatLinkHoverTarget } from './ChatLinkHoverPreview'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'
import type { LinkRect } from './PlaceHoverPreview/types'

interface UseChatLinkInteractionsOptions {
  markdownContainerRef: RefObject<HTMLDivElement | null>
  canShowHover: boolean
  contentKey: unknown
  failedPlaceQueries: ReadonlySet<string>
  messageId?: string
  onOfferClick: (data: { offerId: string; offerType?: string }) => void
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onPlaceSearchClick: (data: { searchQuery: string; placeName: string }) => void
}

function getLinkRect(element: Element): LinkRect {
  const rect = element.getBoundingClientRect()
  return {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  }
}

export function useChatLinkInteractions({
  markdownContainerRef,
  canShowHover,
  contentKey,
  failedPlaceQueries,
  messageId,
  onOfferClick,
  onPlaceClick,
  onPlaceSearchClick,
}: UseChatLinkInteractionsOptions) {
  const [hoverTarget, setHoverTarget] = useState<ChatLinkHoverTarget | undefined>()
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = undefined
    }
  }, [])

  const openHoverTarget = useCallback((target: ChatLinkHoverTarget) => {
    clearHoverCloseTimeout()
    setHoverTarget(target)
  }, [clearHoverCloseTimeout])

  const scheduleHoverClose = useCallback(() => {
    clearHoverCloseTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverTarget(undefined)
      hoverCloseTimeoutRef.current = undefined
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverCloseTimeout])

  const closeHoverTarget = useCallback(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    return () => clearHoverCloseTimeout()
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [
    canShowHover,
    clearHoverCloseTimeout,
    contentKey,
    failedPlaceQueries,
    messageId,
  ])

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return
    const cleanups: Array<() => void> = []

    container.querySelectorAll('.place-tag').forEach(tag => {
      const htmlTag = tag as HTMLElement
      const placeSearch = htmlTag.dataset.placeSearch

      if (placeSearch) {
        if (failedPlaceQueries.has(placeSearch)) {
          htmlTag.classList.add('place-tag--failed')
          return
        }
        htmlTag.classList.remove('place-tag--failed')
      }

      const clickHandler = (event: Event) => {
        const target = event.currentTarget as HTMLElement
        const placeId = target.dataset.placeId
        const placeSearchQuery = target.dataset.placeSearch
        const placeName = target.textContent || target.dataset.placeName || ''
        if (placeId) {
          onPlaceClick({ googlePlaceId: placeId, placeName })
        } else if (placeSearchQuery) {
          onPlaceSearchClick({ searchQuery: placeSearchQuery, placeName })
        }
      }

      const hoverEnterHandler = (event: Event) => {
        const target = event.currentTarget as HTMLElement
        const placeId = target.dataset.placeId
        const placeSearchQuery = target.dataset.placeSearch
        const placeName = target.textContent || target.dataset.placeName || ''

        if (placeId) {
          openHoverTarget({
            type: 'place',
            placeId,
            placeName,
            rect: getLinkRect(target),
          })
          return
        }

        if (placeSearchQuery) {
          openHoverTarget({
            type: 'placeSearch',
            searchQuery: placeSearchQuery,
            placeName,
            rect: getLinkRect(target),
          })
        }
      }

      tag.addEventListener('click', clickHandler)
      cleanups.push(() => tag.removeEventListener('click', clickHandler))

      if (canShowHover) {
        tag.addEventListener('mouseenter', hoverEnterHandler)
        tag.addEventListener('mouseleave', scheduleHoverClose)
        cleanups.push(() => tag.removeEventListener('mouseenter', hoverEnterHandler))
        cleanups.push(() => tag.removeEventListener('mouseleave', scheduleHoverClose))
      }
    })

    container.querySelectorAll('.offer-tag').forEach(tag => {
      const clickHandler = (event: Event) => {
        const target = event.currentTarget as HTMLElement
        const offerId = target.dataset.offerId
        const offerType = target.dataset.offerType
        if (offerId) {
          onOfferClick({ offerId, offerType })
        }
      }

      const hoverEnterHandler = (event: Event) => {
        const target = event.currentTarget as HTMLElement
        const offerId = target.dataset.offerId
        const offerType = target.dataset.offerType
        const offerName = target.textContent || target.dataset.offerName || ''
        if (!offerId || !isHoverPreviewOfferType(offerType)) return

        openHoverTarget({
          type: 'offer',
          offerId,
          offerType,
          offerName,
          rect: getLinkRect(target),
        })
      }

      tag.addEventListener('click', clickHandler)
      cleanups.push(() => tag.removeEventListener('click', clickHandler))

      if (canShowHover) {
        tag.addEventListener('mouseenter', hoverEnterHandler)
        tag.addEventListener('mouseleave', scheduleHoverClose)
        cleanups.push(() => tag.removeEventListener('mouseenter', hoverEnterHandler))
        cleanups.push(() => tag.removeEventListener('mouseleave', scheduleHoverClose))
      }
    })

    return () => cleanups.forEach(fn => fn())
  }, [
    canShowHover,
    contentKey,
    failedPlaceQueries,
    markdownContainerRef,
    messageId,
    onOfferClick,
    onPlaceClick,
    onPlaceSearchClick,
    openHoverTarget,
    scheduleHoverClose,
  ])

  return {
    hoverTarget,
    closeHoverTarget,
    keepHoverTargetOpen: clearHoverCloseTimeout,
  } as const
}
